import React, { useState, useEffect, useRef } from 'react';
import './AdminPanel.css';
import { TAG_BG, TAG_EMOJIS } from './blogStore';
import {
  login as apiLogin, isLoggedIn, logout as apiLogout,
  fetchAllPostsAdmin, createPost, updatePost, deletePost,
  togglePin, toggleStatus, duplicatePost,
  fetchSettings, saveSettingsApi,
  fetchMedia, uploadMedia, deleteMedia,
} from '../services/api';

const DEFAULT_SETTINGS = {
  siteTitle: 'CropGuard AI', tagline: '', heroTitle: '', heroSubtitle: '',
  primaryColor: '#52b788', contactEmail: '', contactPhone: '',
  socialFacebook: '', socialTwitter: '', socialInstagram: '',
  footerText: '', postsPerPage: 6, maintenanceMode: false, analyticsEnabled: true,
};

const BLANK_FORM = {
  tag: 'Tomato', title: '', excerpt: '', readTime: '5 min',
  content: '', author: 'CropGuard Team', status: 'published',
  pinned: false, tags: '', featuredImg: '', cardImg: '',
};

/* ── BLANK Simple Content ── */
const BLANK_SIMPLE = {
  intro: '',
  sections: [{ heading: '', bullets: [''] }],
};

/* ── Convert simple blocks → HTML content string ── */
function simpleToHtml(simple) {
  let html = '';
  if (simple.intro?.trim()) {
    html += `<p>${simple.intro.trim()}</p>\n\n`;
  }
  simple.sections.forEach(sec => {
    if (sec.heading?.trim()) {
      html += `<h4>${sec.heading.trim()}</h4>\n`;
    }
    const validBullets = sec.bullets.filter(b => b.trim());
    if (validBullets.length > 0) {
      html += `<ul>\n${validBullets.map(b => `  <li>${b.trim()}</li>`).join('\n')}\n</ul>\n\n`;
    }
  });
  return html.trim();
}

/* ── Simple Post Editor Component ── */
function SimpleEditor({ simple, onChange }) {
  const updateIntro = (val) => onChange({ ...simple, intro: val });

  const updateSection = (idx, field, val) => {
    const sections = simple.sections.map((s, i) => i === idx ? { ...s, [field]: val } : s);
    onChange({ ...simple, sections });
  };

  const updateBullet = (sIdx, bIdx, val) => {
    const sections = simple.sections.map((s, i) => {
      if (i !== sIdx) return s;
      const bullets = s.bullets.map((b, j) => j === bIdx ? val : b);
      return { ...s, bullets };
    });
    onChange({ ...simple, sections });
  };

  const addBullet = (sIdx) => {
    const sections = simple.sections.map((s, i) =>
      i === sIdx ? { ...s, bullets: [...s.bullets, ''] } : s
    );
    onChange({ ...simple, sections });
  };

  const removeBullet = (sIdx, bIdx) => {
    const sections = simple.sections.map((s, i) => {
      if (i !== sIdx) return s;
      const bullets = s.bullets.filter((_, j) => j !== bIdx);
      return { ...s, bullets: bullets.length ? bullets : [''] };
    });
    onChange({ ...simple, sections });
  };

  const addSection = () => {
    onChange({ ...simple, sections: [...simple.sections, { heading: '', bullets: [''] }] });
  };

  const removeSection = (idx) => {
    if (simple.sections.length === 1) return;
    onChange({ ...simple, sections: simple.sections.filter((_, i) => i !== idx) });
  };

  const inputStyle = {
    background: 'rgba(255,255,255,.05)',
    border: '1px solid rgba(82,183,136,.25)',
    borderRadius: 8,
    color: 'var(--cream)',
    padding: '10px 14px',
    fontSize: '.88rem',
    fontFamily: 'DM Sans,sans-serif',
    width: '100%',
    outline: 'none',
    marginBottom: 8,
  };

  return (
    <div>
      {/* Intro paragraph */}
      <label style={{ display: 'block', fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--green-accent)', marginBottom: 6, fontWeight: 600 }}>
        Opening Paragraph
      </label>
      <textarea
        rows={3}
        style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
        placeholder="Write a short introduction to your article here..."
        value={simple.intro}
        onChange={e => updateIntro(e.target.value)}
      />

      {/* Sections */}
      {simple.sections.map((sec, sIdx) => (
        <div key={sIdx} style={{ border: '1px solid rgba(82,183,136,.18)', borderRadius: 12, padding: '16px 16px 10px', marginBottom: 14, background: 'rgba(82,183,136,.04)', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <label style={{ fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--green-accent)', fontWeight: 600, margin: 0 }}>
              Section {sIdx + 1}
            </label>
            {simple.sections.length > 1 && (
              <button onClick={() => removeSection(sIdx)} style={{ background: 'rgba(255,100,100,.12)', border: '1px solid rgba(255,100,100,.25)', borderRadius: 6, color: '#ff8080', padding: '3px 10px', cursor: 'pointer', fontSize: '.75rem', fontFamily: 'DM Sans,sans-serif' }}>
                <i className="bi bi-trash3"></i> Remove
              </button>
            )}
          </div>

          <input
            style={inputStyle}
            placeholder="Section heading (e.g. Symptoms to Look For)"
            value={sec.heading}
            onChange={e => updateSection(sIdx, 'heading', e.target.value)}
          />

          <label style={{ fontSize: '.72rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
            <i className="bi bi-list-ul me-1"></i>Bullet Points
          </label>

          {sec.bullets.map((bullet, bIdx) => (
            <div key={bIdx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <span style={{ color: 'var(--green-accent)', fontSize: '1rem', lineHeight: '38px', flexShrink: 0 }}>•</span>
              <input
                style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                placeholder={`Point ${bIdx + 1}...`}
                value={bullet}
                onChange={e => updateBullet(sIdx, bIdx, e.target.value)}
              />
              {sec.bullets.length > 1 && (
                <button onClick={() => removeBullet(sIdx, bIdx)} style={{ background: 'none', border: '1px solid rgba(255,100,100,.2)', borderRadius: 6, color: '#ff8080', padding: '0 10px', cursor: 'pointer', flexShrink: 0 }}>
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          ))}

          <button onClick={() => addBullet(sIdx)} style={{ background: 'none', border: '1px dashed rgba(82,183,136,.35)', borderRadius: 7, color: 'var(--green-accent)', padding: '6px 14px', cursor: 'pointer', fontSize: '.8rem', fontFamily: 'DM Sans,sans-serif', marginTop: 4 }}>
            <i className="bi bi-plus-circle me-1"></i>Add Bullet Point
          </button>
        </div>
      ))}

      <button onClick={addSection} style={{ background: 'rgba(82,183,136,.08)', border: '1px dashed rgba(82,183,136,.4)', borderRadius: 10, color: 'var(--green-accent)', padding: '10px 20px', cursor: 'pointer', fontSize: '.85rem', fontFamily: 'DM Sans,sans-serif', width: '100%' }}>
        <i className="bi bi-plus-lg me-2"></i>Add New Section
      </button>
    </div>
  );
}

/* ── Toast ── */
function Toast({ msg, type = 'success', onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2800); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className={`admin-toast ${type === 'error' ? 'admin-toast-error' : ''}`}>
      <i className={`bi ${type === 'error' ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill'}`}
        style={{ color: type === 'error' ? '#ff6b6b' : 'var(--green-accent)', fontSize: '1.1rem' }}></i>
      {msg}
    </div>
  );
}

/* ── Stat Card ── */
function StatCard({ icon, label, val, sub, color = 'var(--green-accent)' }) {
  return (
    <div className="admin-stat">
      <i className={`bi ${icon}`} style={{ color, fontSize: '1.4rem', marginBottom: 6, display: 'block' }}></i>
      <div className="admin-stat-num" style={{ color }}>{val}</div>
      <p className="admin-stat-label">{label}</p>
      {sub && <p className="admin-stat-sub">{sub}</p>}
    </div>
  );
}

export default function AdminPanel({ onClose }) {
  const [authed,    setAuthed]    = useState(isLoggedIn());
  const [passInput, setPassInput] = useState('');
  const [passErr,   setPassErr]   = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editorMode, setEditorMode] = useState('simple');
  const [simpleContent, setSimpleContent] = useState(BLANK_SIMPLE);

  const [posts,     setPosts]     = useState([]);
  const [form,      setForm]      = useState(BLANK_FORM);
  const [editId,    setEditId]    = useState(null);
  const [search,    setSearch]    = useState('');
  const [filterTag, setFilterTag] = useState('All');
  const [filterSt,  setFilterSt]  = useState('All');
  const [sortBy,    setSortBy]    = useState('newest');
  const [confirmId, setConfirmId] = useState(null);
  const [toast,     setToast]     = useState({ msg: '', type: 'success' });

  const [settings,  setSettings]  = useState(DEFAULT_SETTINGS);
  const [settForm,  setSettForm]  = useState(DEFAULT_SETTINGS);

  const [media,     setMedia]     = useState([]);
  const [mediaView, setMediaView] = useState(null);
  const fileRef    = useRef();
  const featImgRef  = useRef();
  const cardImgRef  = useRef();
  const inArtRef    = useRef();
  const [imgPickerFor, setImgPickerFor] = useState(null); // 'featured' | 'card' | 'inarticle'
  const [showImgPicker, setShowImgPicker] = useState(false);

  const notify = (msg, type = 'success') => setToast({ msg, type });
  const notifyUpdate = () => window.dispatchEvent(new Event('cropguard_posts_updated'));

  useEffect(() => {
    if (authed) {
      fetchAllPostsAdmin().then(setPosts).catch(() => notify('Posts load nahi ho sake.', 'error'));
      fetchMedia().then(setMedia).catch(() => {});
      fetchSettings().then(s => { setSettings(s); setSettForm(s); }).catch(() => {});
    }
  }, [authed]);

  /* ── Auth ── */
  const handleLogin = async () => {
    setLoggingIn(true);
    try {
      await apiLogin(passInput);
      setAuthed(true);
      setPassErr(false);
    } catch (e) {
      setPassErr(true);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    apiLogout();
    setAuthed(false);
    onClose();
  };

  /* ── Posts helpers ── */
  const filteredPosts = posts
    .filter(p => {
      const q = search.toLowerCase();
      const matchQ = !q || p.title.toLowerCase().includes(q) || (p.author || '').toLowerCase().includes(q);
      const matchTag = filterTag === 'All' || p.tag === filterTag;
      const matchSt  = filterSt  === 'All' || p.status === filterSt;
      return matchQ && matchTag && matchSt;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return b.id - a.id;
      if (sortBy === 'oldest') return a.id - b.id;
      if (sortBy === 'views')  return (b.views || 0) - (a.views || 0);
      if (sortBy === 'title')  return a.title.localeCompare(b.title);
      return 0;
    });

  const allTags = ['All', ...Array.from(new Set(posts.map(p => p.tag)))];
  const publishedCount = posts.filter(p => p.status === 'published').length;
  const draftCount     = posts.filter(p => p.status === 'draft').length;
  const totalViews     = posts.reduce((s, p) => s + (p.views || 0), 0);
  const pinnedCount    = posts.filter(p => p.pinned).length;

  const handleSave = async () => {
    // In simple mode, generate HTML from blocks first
    const finalContent = editorMode === 'simple'
      ? simpleToHtml(simpleContent)
      : form.content;

    if (!form.title.trim() || !form.excerpt.trim() || !finalContent.trim()) {
      notify('Title, excerpt, and content are required.', 'error'); return;
    }
    const tagsArr = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
    const payload = {
      ...form,
      content: finalContent,
      tags: tagsArr,
      emoji: TAG_EMOJIS[form.tag] || '🌿',
      bg: TAG_BG[form.tag] || TAG_BG.General,
    };
    try {
      let saved;
      if (editId !== null) {
        saved = await updatePost(editId, payload);
        setPosts(posts.map(p => p.id === editId ? saved : p));
      } else {
        saved = await createPost(payload);
        setPosts([saved, ...posts]);
      }
      notifyUpdate();
      setForm(BLANK_FORM);
      setSimpleContent(BLANK_SIMPLE);
      setEditorMode('simple');
      setEditId(null);
      setActiveTab('posts');
      notify(editId !== null ? 'Post updated successfully!' : form.status === 'draft' ? 'Draft saved!' : 'Post published!');
    } catch (e) {
      notify(e.message || 'Save failed.', 'error');
    }
  };

  const handleEdit = (post) => {
    setForm({
      tag: post.tag, title: post.title, excerpt: post.excerpt,
      readTime: post.readTime, content: post.content,
      author: post.author || 'CropGuard Team',
      status: post.status || 'published',
      pinned: post.pinned || false,
      tags: (post.tags || []).join(', '),
      featuredImg: post.featuredImg || '',
      cardImg: post.cardImg || '',
    });
    setEditId(post.id);
    setActiveTab('editor');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    try {
      await deletePost(id);
      setPosts(posts.filter(p => p.id !== id));
      notifyUpdate();
      notify('Post deleted.');
    } catch (e) {
      notify(e.message || 'Delete failed.', 'error');
    } finally {
      setConfirmId(null);
    }
  };

  const handleTogglePin = async (id) => {
    try {
      const saved = await togglePin(id);
      setPosts(posts.map(p => p.id === id ? saved : p));
      notifyUpdate();
      notify('Pin status updated!');
    } catch (e) { notify(e.message || 'Failed.', 'error'); }
  };

  const handleToggleStatus = async (id) => {
    try {
      const saved = await toggleStatus(id);
      setPosts(posts.map(p => p.id === id ? saved : p));
      notifyUpdate();
      notify('Status updated!');
    } catch (e) { notify(e.message || 'Failed.', 'error'); }
  };

  const handleDuplicate = async (post) => {
    try {
      const saved = await duplicatePost(post.id);
      setPosts([saved, ...posts]);
      notifyUpdate();
      notify('Post duplicated!');
    } catch (e) { notify(e.message || 'Failed.', 'error'); }
  };

  const handleBulkDelete = async (ids) => {
    try {
      await Promise.all(ids.map(id => deletePost(id)));
      setPosts(posts.filter(p => !ids.includes(p.id)));
      notifyUpdate();
      notify(`${ids.length} posts deleted.`);
    } catch (e) { notify(e.message || 'Delete failed.', 'error'); }
  };

  /* ── Settings ── */
  const handleSaveSettings = async () => {
    try {
      const saved = await saveSettingsApi(settForm);
      setSettings(saved);
      notify('Settings saved!');
    } catch (e) { notify(e.message || 'Save failed.', 'error'); }
  };

  /* ── Media ── */
  const handleMediaUpload = (e) => {
    const files = Array.from(e.target.files);
    Promise.all(files.map(file => uploadMedia(file)))
      .then(items => { setMedia(prev => [...items, ...prev]); notify('Media uploaded!'); })
      .catch(err => notify(err.message || 'Upload failed.', 'error'));
  };

  const handleDeleteMedia = async (id) => {
    try {
      await deleteMedia(id);
      setMedia(media.filter(m => m.id !== id));
      notify('Media deleted.');
    } catch (e) { notify(e.message || 'Delete failed.', 'error'); }
  };

  /* ── Image upload (goes to backend, returns a real URL) ── */
  const uploadImgFor = (target, e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadMedia(file)
      .then(item => {
        setMedia(prev => [item, ...prev]);
        if (target === 'featured') setForm(f => ({ ...f, featuredImg: item.data }));
        if (target === 'card')     setForm(f => ({ ...f, cardImg: item.data }));
        notify('Image uploaded!');
      })
      .catch(err => notify(err.message || 'Upload failed.', 'error'));
  };

  const insertImgToContent = (src) => {
    const tag = `\n<img src="${src}" alt="Article image" style="width:100%;border-radius:12px;margin:16px 0;" />\n`;
    setForm(f => ({ ...f, content: f.content + tag }));
    setShowImgPicker(false);
    notify('Image inserted into article!');
  };

  /* ── Login Screen ── */
  if (!authed) {
    return (
      <div className="admin-overlay" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="admin-login-box">
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔐</div>
          <h3 style={{ fontFamily: 'Playfair Display,serif', color: 'var(--cream)', marginBottom: 8 }}>Admin Login</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '.9rem', marginBottom: 28 }}>
            Enter your password to access the CropGuard admin panel.
          </p>
          <input
            type="password"
            className="admin-input"
            placeholder="Enter admin password"
            value={passInput}
            onChange={e => { setPassInput(e.target.value); setPassErr(false); }}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{ marginBottom: 10 }}
          />
          {passErr && <div style={{ color: '#ff6b6b', fontSize: '.82rem', marginBottom: 10 }}>❌ Incorrect password.</div>}
          <button className="admin-submit-btn w-100 justify-content-center mt-2" onClick={handleLogin} disabled={loggingIn}>
            <i className="bi bi-unlock"></i> {loggingIn ? 'Logging in...' : 'Login to Admin'}
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', marginTop: 16, cursor: 'pointer', fontSize: '.85rem', fontFamily: 'DM Sans,sans-serif' }}>
            ← Back to Website
          </button>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'dashboard', icon: 'bi-grid-1x2', label: 'Dashboard' },
    { id: 'posts',     icon: 'bi-file-earmark-text', label: 'Posts' },
    { id: 'editor',    icon: 'bi-plus-circle', label: editId ? 'Edit Post' : 'New Post' },
    { id: 'media',     icon: 'bi-images', label: 'Media' },
    { id: 'settings',  icon: 'bi-gear', label: 'Settings' },
  ];

  return (
    <div className="admin-overlay">

      {/* ── Sidebar ── */}
      <div className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <i className="bi bi-shield-check"></i>
          <span>CropGuard</span>
          <span className="admin-badge">Admin</span>
        </div>

        <nav className="admin-sidebar-nav">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`admin-nav-item ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => {
                if (t.id === 'editor' && activeTab !== 'editor') { setEditId(null); setForm(BLANK_FORM); setSimpleContent(BLANK_SIMPLE); setEditorMode('simple'); }
                setActiveTab(t.id);
              }}
            >
              <i className={`bi ${t.icon}`}></i>
              <span>{t.label}</span>
            </button>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 10, padding: '0 8px' }}>
            <i className="bi bi-circle-fill me-1" style={{ fontSize: '.5rem', color: '#52b788' }}></i>
            Logged in as Admin
          </div>
          <button className="admin-nav-item" onClick={handleLogout} style={{ color: '#ff6b6b' }}>
            <i className="bi bi-box-arrow-right"></i>
            <span>Exit Admin</span>
          </button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="admin-main">

        {/* ══════════════ DASHBOARD ══════════════ */}
        {activeTab === 'dashboard' && (
          <div className="admin-page">
            <div className="admin-page-header">
              <h2 className="admin-page-title">
                <i className="bi bi-grid-1x2"></i> Dashboard
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '.9rem', margin: 0 }}>
                Welcome back! Here's your CropGuard overview.
              </p>
            </div>

            {/* Stats */}
            <div className="row g-3 mb-4">
              <div className="col-6 col-xl-3">
                <StatCard icon="bi-file-earmark-check" label="Published Posts" val={publishedCount} color="var(--green-accent)" />
              </div>
              <div className="col-6 col-xl-3">
                <StatCard icon="bi-file-earmark-minus" label="Drafts" val={draftCount} color="var(--gold-light)" />
              </div>
              <div className="col-6 col-xl-3">
                <StatCard icon="bi-eye" label="Total Views" val={totalViews.toLocaleString()} color="#7ec8e3" />
              </div>
              <div className="col-6 col-xl-3">
                <StatCard icon="bi-pin-angle" label="Pinned Posts" val={pinnedCount} color="#d88" />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="admin-panel-box mb-4">
              <div className="admin-panel-title">
                <i className="bi bi-lightning-charge"></i> Quick Actions
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {[
                  { label: 'New Post', icon: 'bi-plus-circle', action: () => { setForm(BLANK_FORM); setSimpleContent(BLANK_SIMPLE); setEditorMode('simple'); setEditId(null); setActiveTab('editor'); }, color: 'var(--green-accent)' },
                  { label: 'View All Posts', icon: 'bi-list-ul', action: () => setActiveTab('posts'), color: '#7ec8e3' },
                  { label: 'Upload Media', icon: 'bi-cloud-upload', action: () => setActiveTab('media'), color: 'var(--gold-light)' },
                  { label: 'Site Settings', icon: 'bi-gear', action: () => setActiveTab('settings'), color: '#d88' },
                ].map(q => (
                  <button key={q.label} onClick={q.action} className="admin-quick-btn" style={{ '--qc': q.color }}>
                    <i className={`bi ${q.icon}`}></i> {q.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Posts */}
            <div className="admin-panel-box">
              <div className="admin-panel-title">
                <i className="bi bi-clock-history"></i> Recent Posts
              </div>
              {posts.slice(0, 5).map(p => (
                <div className="admin-post-row" key={p.id}>
                  <div className="admin-post-emoji" style={{ background: p.bg }}>{p.emoji}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="admin-post-title">{p.title}</div>
                    <div className="admin-post-meta">
                      <span className="blog-tag me-2" style={{ fontSize: '.68rem' }}>{p.tag}</span>
                      <span className={`admin-status-badge admin-status-${p.status || 'published'}`}>{p.status || 'published'}</span>
                      &nbsp;·&nbsp;<i className="bi bi-eye me-1"></i>{(p.views || 0).toLocaleString()} views
                    </div>
                  </div>
                  <button className="admin-action-btn admin-edit-btn" onClick={() => handleEdit(p)}>
                    <i className="bi bi-pencil"></i> Edit
                  </button>
                </div>
              ))}
            </div>

            {/* Category Breakdown */}
            <div className="admin-panel-box mt-4">
              <div className="admin-panel-title"><i className="bi bi-pie-chart"></i> Category Breakdown</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {Object.entries(posts.reduce((acc, p) => { acc[p.tag] = (acc[p.tag] || 0) + 1; return acc; }, {}))
                  .map(([tag, count]) => (
                    <div key={tag} style={{
                      background: 'rgba(82,183,136,.08)', border: '1px solid rgba(82,183,136,.2)',
                      borderRadius: 12, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8
                    }}>
                      <span style={{ fontSize: '1.2rem' }}>{TAG_EMOJIS[tag] || '🌿'}</span>
                      <div>
                        <div style={{ color: 'var(--cream)', fontWeight: 600, fontSize: '.88rem' }}>{tag}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>{count} posts</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ ALL POSTS ══════════════ */}
        {activeTab === 'posts' && (
          <div className="admin-page">
            <div className="admin-page-header">
              <h2 className="admin-page-title"><i className="bi bi-file-earmark-text"></i> All Posts</h2>
              <button className="admin-submit-btn" onClick={() => { setForm(BLANK_FORM); setSimpleContent(BLANK_SIMPLE); setEditorMode('simple'); setEditId(null); setActiveTab('editor'); }}>
                <i className="bi bi-plus-circle"></i> New Post
              </button>
            </div>

            {/* Filters Bar */}
            <div className="admin-panel-box mb-3" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1 1 200px' }}>
                  <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '.85rem' }}></i>
                  <input
                    className="admin-input"
                    placeholder="Search posts..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ paddingLeft: 34 }}
                  />
                </div>
                <select className="admin-input" style={{ flex: '0 0 auto', width: 'auto', minWidth: 120 }} value={filterTag} onChange={e => setFilterTag(e.target.value)}>
                  {allTags.map(t => <option key={t}>{t}</option>)}
                </select>
                <select className="admin-input" style={{ flex: '0 0 auto', width: 'auto', minWidth: 130 }} value={filterSt} onChange={e => setFilterSt(e.target.value)}>
                  <option value="All">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
                <select className="admin-input" style={{ flex: '0 0 auto', width: 'auto', minWidth: 130 }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="views">Most Viewed</option>
                  <option value="title">A–Z Title</option>
                </select>
              </div>
              <div style={{ marginTop: 10, fontSize: '.8rem', color: 'var(--text-muted)' }}>
                Showing <strong style={{ color: 'var(--green-accent)' }}>{filteredPosts.length}</strong> of {posts.length} posts
              </div>
            </div>

            {/* Posts List */}
            <div className="admin-panel-box">
              {filteredPosts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: 12 }}>📭</div>
                  No posts found.
                </div>
              ) : (
                filteredPosts.map(p => (
                  <div className="admin-post-row" key={p.id}>
                    <div className="admin-post-emoji" style={{ background: p.bg }}>{p.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="admin-post-title">
                        {p.pinned && <i className="bi bi-pin-fill me-1" style={{ color: '#d88', fontSize: '.75rem' }}></i>}
                        {p.title}
                      </div>
                      <div className="admin-post-meta">
                        <span className="blog-tag me-2" style={{ fontSize: '.68rem' }}>{p.tag}</span>
                        <span className={`admin-status-badge admin-status-${p.status || 'published'}`}>{p.status || 'published'}</span>
                        &nbsp;·&nbsp;<i className="bi bi-calendar3 me-1"></i>{p.date}
                        &nbsp;·&nbsp;<i className="bi bi-eye me-1"></i>{(p.views || 0).toLocaleString()}
                        &nbsp;·&nbsp;<i className="bi bi-person me-1"></i>{p.author || '—'}
                      </div>
                    </div>
                    <div className="d-flex gap-1 flex-shrink-0 flex-wrap justify-content-end">
                      <button className="admin-action-btn admin-edit-btn" onClick={() => handleEdit(p)} title="Edit">
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="admin-action-btn" onClick={() => handleToggleStatus(p.id)}
                        style={{ borderColor: 'rgba(212,160,23,.35)', color: 'var(--gold-light)' }} title="Toggle status">
                        <i className={`bi ${p.status === 'draft' ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                      </button>
                      <button className="admin-action-btn" onClick={() => handleTogglePin(p.id)}
                        style={{ borderColor: 'rgba(220,136,136,.35)', color: '#d88' }} title="Pin/Unpin">
                        <i className={`bi ${p.pinned ? 'bi-pin-fill' : 'bi-pin'}`}></i>
                      </button>
                      <button className="admin-action-btn" onClick={() => handleDuplicate(p)}
                        style={{ borderColor: 'rgba(126,200,227,.3)', color: '#7ec8e3' }} title="Duplicate">
                        <i className="bi bi-copy"></i>
                      </button>
                      <button className="admin-action-btn admin-delete-btn" onClick={() => setConfirmId(p.id)} title="Delete">
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ══════════════ EDITOR ══════════════ */}
        {activeTab === 'editor' && (
          <div className="admin-page">
            <div className="admin-page-header">
              <h2 className="admin-page-title">
                <i className={`bi ${editId ? 'bi-pencil-square' : 'bi-plus-circle'}`}></i>
                {editId ? 'Edit Post' : 'Create New Post'}
              </h2>
              {editId && (
                <button onClick={() => { setEditId(null); setForm(BLANK_FORM); setSimpleContent(BLANK_SIMPLE); setEditorMode('simple'); setActiveTab('posts'); }}
                  style={{ background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 10, color: 'var(--text-muted)', padding: '8px 16px', cursor: 'pointer', fontFamily: 'DM Sans,sans-serif', fontSize: '.85rem' }}>
                  Cancel Edit
                </button>
              )}
            </div>

            <div className="row g-4">
              {/* Left: Main */}
              <div className="col-lg-8">
                <div className="admin-panel-box mb-3">
                  <label className="admin-label">Post Title *</label>
                  <input className="admin-input" placeholder="e.g. How to Prevent Late Blight in Tomatoes"
                    value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                </div>
                <div className="admin-panel-box mb-3">
                  <label className="admin-label">Short Excerpt *</label>
                  <textarea className="admin-input" style={{ minHeight: 80 }}
                    placeholder="A brief 1–2 sentence summary shown on the blog card..."
                    value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} />
                </div>
                <div className="admin-panel-box">
                  {/* Mode Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <label className="admin-label" style={{ margin: 0 }}>Article Content *</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => {
                          if (editorMode === 'advanced') {
                            setEditorMode('simple');
                          }
                        }}
                        style={{
                          padding: '5px 14px', borderRadius: 7, border: '1px solid',
                          borderColor: editorMode === 'simple' ? 'var(--green-accent)' : 'rgba(82,183,136,.25)',
                          background: editorMode === 'simple' ? 'rgba(82,183,136,.15)' : 'none',
                          color: editorMode === 'simple' ? 'var(--green-accent)' : 'var(--text-muted)',
                          cursor: 'pointer', fontSize: '.78rem', fontFamily: 'DM Sans,sans-serif',
                        }}
                      >
                        <i className="bi bi-layout-text-window me-1"></i>Simple
                      </button>
                      <button
                        onClick={() => {
                          if (editorMode === 'simple') {
                            // sync simple → HTML before switching
                            const html = simpleToHtml(simpleContent);
                            if (html && !form.content) {
                              setForm(f => ({ ...f, content: html }));
                            }
                            setEditorMode('advanced');
                          }
                        }}
                        style={{
                          padding: '5px 14px', borderRadius: 7, border: '1px solid',
                          borderColor: editorMode === 'advanced' ? 'var(--green-accent)' : 'rgba(82,183,136,.25)',
                          background: editorMode === 'advanced' ? 'rgba(82,183,136,.15)' : 'none',
                          color: editorMode === 'advanced' ? 'var(--green-accent)' : 'var(--text-muted)',
                          cursor: 'pointer', fontSize: '.78rem', fontFamily: 'DM Sans,sans-serif',
                        }}
                      >
                        <i className="bi bi-code-slash me-1"></i>Advanced (HTML)
                      </button>
                    </div>
                  </div>

                  {/* Simple mode: no HTML needed */}
                  {editorMode === 'simple' && (
                    <>
                      <div style={{ background: 'rgba(82,183,136,.06)', border: '1px solid rgba(82,183,136,.15)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '.8rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        <i className="bi bi-info-circle me-2" style={{ color: 'var(--green-accent)' }}></i>
                        <strong style={{ color: 'var(--cream)' }}>Simple mode:</strong> Write your article in plain text — no coding needed. Add an introduction, then create sections with bullet points. CropGuard AI will format it automatically.
                      </div>
                      <SimpleEditor simple={simpleContent} onChange={setSimpleContent} />
                    </>
                  )}

                  {/* Advanced mode: raw HTML textarea */}
                  {editorMode === 'advanced' && (
                    <>
                      <div style={{ background: 'rgba(212,160,23,.06)', border: '1px solid rgba(212,160,23,.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '.8rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        <i className="bi bi-code-slash me-2" style={{ color: 'var(--gold-light)' }}></i>
                        <strong style={{ color: 'var(--cream)' }}>Advanced mode:</strong> HTML is supported — use{' '}
                        <code style={{ background: 'rgba(255,255,255,.08)', padding: '1px 5px', borderRadius: 4 }}>&lt;h4&gt;</code>,{' '}
                        <code style={{ background: 'rgba(255,255,255,.08)', padding: '1px 5px', borderRadius: 4 }}>&lt;ul&gt;&lt;li&gt;</code>,{' '}
                        <code style={{ background: 'rgba(255,255,255,.08)', padding: '1px 5px', borderRadius: 4 }}>&lt;strong&gt;</code>,{' '}
                        <code style={{ background: 'rgba(255,255,255,.08)', padding: '1px 5px', borderRadius: 4 }}>&lt;p&gt;</code>
                      </div>
                      <textarea
                        className="admin-input admin-content-area"
                        placeholder={`Write your full article content here...\n\n<h4>Section Title</h4>\n<ul>\n  <li>Point 1</li>\n  <li>Point 2</li>\n</ul>\n<strong>Important term</strong>`}
                        value={form.content}
                        onChange={e => setForm({ ...form, content: e.target.value })}
                      />
                    </>
                  )}

                  {/* Word count */}
                  {(form.content || simpleToHtml(simpleContent)) && (
                    <div style={{ marginTop: 12, fontSize: '.78rem', color: 'var(--text-muted)' }}>
                      <i className="bi bi-text-paragraph me-1"></i>
                      {(editorMode === 'simple' ? simpleToHtml(simpleContent) : form.content)
                        .replace(/<[^>]+>/g, ' ')
                        .split(/\s+/).filter(Boolean).length} words
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Meta */}
              <div className="col-lg-4">
                <div className="admin-panel-box mb-3">
                  <div className="admin-panel-title" style={{ fontSize: '1rem', marginBottom: 16 }}>
                    <i className="bi bi-sliders"></i> Post Settings
                  </div>

                  <label className="admin-label">Status</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    {['published', 'draft'].map(s => (
                      <button key={s} onClick={() => setForm({ ...form, status: s })}
                        className="admin-action-btn"
                        style={{
                          flex: 1, justifyContent: 'center',
                          borderColor: form.status === s ? 'var(--green-accent)' : 'rgba(82,183,136,.2)',
                          color: form.status === s ? 'var(--green-accent)' : 'var(--text-muted)',
                          background: form.status === s ? 'rgba(82,183,136,.1)' : 'none',
                          textTransform: 'capitalize',
                        }}>
                        <i className={`bi ${s === 'published' ? 'bi-eye' : 'bi-file-earmark-minus'}`}></i>
                        {s}
                      </button>
                    ))}
                  </div>

                  <label className="admin-label">Category</label>
                  <select className="admin-input" style={{ marginBottom: 12 }} value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })}>
                    <option>Tomato</option>
                    <option>Maize</option>
                    <option>Wheat</option>
                    <option>Cotton</option>
                    <option>Technology</option>
                    <option>General</option>
                    <option>Research</option>
                  </select>

                  <label className="admin-label">Author</label>
                  <input className="admin-input" style={{ marginBottom: 12 }} placeholder="Author name"
                    value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} />

                  <label className="admin-label">Read Time</label>
                  <input className="admin-input" style={{ marginBottom: 12 }} placeholder="5 min"
                    value={form.readTime} onChange={e => setForm({ ...form, readTime: e.target.value })} />

                  <label className="admin-label">Tags (comma-separated)</label>
                  <input className="admin-input" style={{ marginBottom: 12 }} placeholder="fungal, prevention, tomato"
                    value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />

                  {/* ── Card Image (thumbnail on blog list) ── */}
                  <label className="admin-label">Card Image (Blog List Thumbnail)</label>
                  <div className="admin-img-upload-box" style={{ marginBottom: 14 }}>
                    {form.cardImg ? (
                      <div style={{ position:'relative' }}>
                        <img src={form.cardImg} alt="card" className="admin-img-preview" />
                        <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
                          <button className="admin-img-btn" onClick={() => cardImgRef.current.click()}>
                            <i className="bi bi-arrow-repeat"></i> Change
                          </button>
                          <button className="admin-img-btn admin-img-btn-danger" onClick={() => setForm(f => ({...f, cardImg:''}))}>
                            <i className="bi bi-trash3"></i> Remove
                          </button>
                          <button className="admin-img-btn" onClick={() => { setImgPickerFor('card'); setShowImgPicker(true); }}>
                            <i className="bi bi-images"></i> From Library
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="admin-img-dropzone" onClick={() => cardImgRef.current.click()}>
                        <i className="bi bi-card-image" style={{ fontSize:'1.8rem', color:'var(--green-accent)' }}></i>
                        <div style={{ marginTop:8, fontSize:'.85rem', color:'var(--text-muted)' }}>Click to upload card image</div>
                        <div style={{ fontSize:'.75rem', color:'var(--text-muted)', marginTop:4 }}>Shown on blog listing cards</div>
                        <button className="admin-img-btn" style={{ marginTop:10 }} onClick={e => { e.stopPropagation(); setImgPickerFor('card'); setShowImgPicker(true); }}>
                          <i className="bi bi-images"></i> Choose from Library
                        </button>
                      </div>
                    )}
                    <input ref={cardImgRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e => uploadImgFor('card', e)} />
                    <input className="admin-input" style={{ marginTop:8, fontSize:'.78rem' }} placeholder="Or paste image URL..."
                      value={form.cardImg} onChange={e => setForm({ ...form, cardImg: e.target.value })} />
                  </div>

                  {/* ── Featured Image (hero in article) ── */}
                  <label className="admin-label">Featured Image (Article Hero)</label>
                  <div className="admin-img-upload-box" style={{ marginBottom: 14 }}>
                    {form.featuredImg ? (
                      <div style={{ position:'relative' }}>
                        <img src={form.featuredImg} alt="featured" className="admin-img-preview" />
                        <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
                          <button className="admin-img-btn" onClick={() => featImgRef.current.click()}>
                            <i className="bi bi-arrow-repeat"></i> Change
                          </button>
                          <button className="admin-img-btn admin-img-btn-danger" onClick={() => setForm(f => ({...f, featuredImg:''}))}>
                            <i className="bi bi-trash3"></i> Remove
                          </button>
                          <button className="admin-img-btn" onClick={() => { setImgPickerFor('featured'); setShowImgPicker(true); }}>
                            <i className="bi bi-images"></i> From Library
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="admin-img-dropzone" onClick={() => featImgRef.current.click()}>
                        <i className="bi bi-image" style={{ fontSize:'1.8rem', color:'var(--green-accent)' }}></i>
                        <div style={{ marginTop:8, fontSize:'.85rem', color:'var(--text-muted)' }}>Click to upload featured image</div>
                        <div style={{ fontSize:'.75rem', color:'var(--text-muted)', marginTop:4 }}>Large hero banner inside article</div>
                        <button className="admin-img-btn" style={{ marginTop:10 }} onClick={e => { e.stopPropagation(); setImgPickerFor('featured'); setShowImgPicker(true); }}>
                          <i className="bi bi-images"></i> Choose from Library
                        </button>
                      </div>
                    )}
                    <input ref={featImgRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e => uploadImgFor('featured', e)} />
                    <input className="admin-input" style={{ marginTop:8, fontSize:'.78rem' }} placeholder="Or paste image URL..."
                      value={form.featuredImg} onChange={e => setForm({ ...form, featuredImg: e.target.value })} />
                  </div>

                  {/* ── In-Article Image Insert ── */}
                  <label className="admin-label">Insert Image into Article Body</label>
                  <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
                    <button className="admin-img-btn" style={{ flex:1 }} onClick={() => inArtRef.current.click()}>
                      <i className="bi bi-cloud-upload"></i> Upload & Insert
                    </button>
                    <button className="admin-img-btn" style={{ flex:1 }} onClick={() => { setImgPickerFor('inarticle'); setShowImgPicker(true); }}>
                      <i className="bi bi-images"></i> From Library
                    </button>
                    <input ref={inArtRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e => {
                      const file = e.target.files[0]; if (!file) return;
                      uploadMedia(file)
                        .then(item => { setMedia(prev => [item, ...prev]); insertImgToContent(item.data); })
                        .catch(err => notify(err.message || 'Upload failed.', 'error'));
                    }} />
                  </div>

                  <label className="admin-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}>
                    <input type="checkbox" checked={form.pinned} onChange={e => setForm({ ...form, pinned: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: 'var(--green-accent)' }} />
                    <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>📌 Pin this post to top</span>
                  </label>
                </div>

                {/* Preview */}
                {form.title && (
                  <div className="admin-panel-box">
                    <div className="admin-panel-title" style={{ fontSize: '1rem', marginBottom: 12 }}>
                      <i className="bi bi-eye"></i> Preview
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ fontSize: '2rem', width: 48, height: 48, borderRadius: 10, background: TAG_BG[form.tag] || TAG_BG.General, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {TAG_EMOJIS[form.tag] || '🌿'}
                      </div>
                      <div>
                        <span className="blog-tag" style={{ fontSize: '.65rem' }}>{form.tag}</span>
                        <div style={{ color: 'var(--cream)', fontWeight: 600, fontSize: '.88rem', marginTop: 4 }}>{form.title}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '.75rem', marginTop: 2 }}>
                          by {form.author || '—'} · {form.readTime}
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 0', borderTop: '1px solid rgba(82,183,136,.1)' }}>
                      <span className={`admin-status-badge admin-status-${form.status}`}>{form.status}</span>
                      {form.pinned && <span style={{ marginLeft: 6, fontSize: '.7rem', color: '#d88' }}>📌 Pinned</span>}
                    </div>
                  </div>
                )}

                {/* Publish button */}
                <button className="admin-submit-btn w-100 justify-content-center mt-3" onClick={handleSave}>
                  <i className={`bi ${editId ? 'bi-check-circle' : form.status === 'draft' ? 'bi-file-earmark-minus' : 'bi-send'}`}></i>
                  {editId ? 'Update Post' : form.status === 'draft' ? 'Save Draft' : 'Publish Post'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ MEDIA LIBRARY ══════════════ */}
        {activeTab === 'media' && (
          <div className="admin-page">
            <div className="admin-page-header">
              <h2 className="admin-page-title"><i className="bi bi-images"></i> Media Library</h2>
              <button className="admin-submit-btn" onClick={() => fileRef.current.click()}>
                <i className="bi bi-cloud-upload"></i> Upload Files
              </button>
              <input ref={fileRef} type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={handleMediaUpload} />
            </div>

            {media.length === 0 ? (
              <div className="admin-panel-box" style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '3rem', marginBottom: 12 }}>🖼️</div>
                <p style={{ color: 'var(--text-muted)' }}>No media uploaded yet.</p>
                <button className="admin-submit-btn mt-3" style={{ margin: '0 auto' }} onClick={() => fileRef.current.click()}>
                  <i className="bi bi-cloud-upload"></i> Upload First Image
                </button>
              </div>
            ) : (
              <div className="admin-panel-box">
                <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                  {media.length} file{media.length !== 1 ? 's' : ''} in library
                </div>
                <div className="admin-media-grid">
                  {media.map(m => (
                    <div key={m.id} className="admin-media-item" onClick={() => setMediaView(m)}>
                      {m.type?.startsWith('image/') ? (
                        <img src={m.data} alt={m.name} />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <i className="bi bi-file-earmark" style={{ fontSize: '2rem', color: 'var(--text-muted)' }}></i>
                        </div>
                      )}
                      <div className="admin-media-overlay">
                        <div style={{ fontSize: '.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                        <div style={{ fontSize: '.65rem', opacity: .7 }}>{m.size}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Media Detail Modal */}
            {mediaView && (
              <div className="admin-confirm" onClick={() => setMediaView(null)}>
                <div className="admin-confirm-box" style={{ maxWidth: 560, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
                  {mediaView.type?.startsWith('image/') && (
                    <img src={mediaView.data} alt={mediaView.name} style={{ width: '100%', borderRadius: 10, marginBottom: 16, maxHeight: 300, objectFit: 'contain' }} />
                  )}
                  <h4 style={{ fontFamily: 'Playfair Display,serif', color: 'var(--cream)', marginBottom: 8 }}>{mediaView.name}</h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 4 }}>Size: {mediaView.size}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 16 }}>Uploaded: {mediaView.uploadedAt}</p>
                  <label className="admin-label">URL / Data URI (copy to use in posts)</label>
                  <input className="admin-input" readOnly value={mediaView.data} onClick={e => e.target.select()} style={{ marginBottom: 16, fontSize: '.75rem' }} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="admin-action-btn admin-delete-btn" style={{ padding: '8px 16px' }} onClick={() => { handleDeleteMedia(mediaView.id); setMediaView(null); }}>
                      <i className="bi bi-trash"></i> Delete
                    </button>
                    <button className="admin-action-btn admin-edit-btn" style={{ padding: '8px 16px' }} onClick={() => setMediaView(null)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════ IMAGE PICKER MODAL ══════════════ */}
        {showImgPicker && (
          <div className="admin-confirm" onClick={() => setShowImgPicker(false)}>
            <div className="admin-confirm-box" style={{ maxWidth:700, textAlign:'left', maxHeight:'80vh', overflow:'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <h3 style={{ margin:0, color:'var(--cream)', fontSize:'1rem' }}>
                  <i className="bi bi-images" style={{ color:'var(--green-accent)', marginRight:8 }}></i>
                  {imgPickerFor === 'inarticle' ? 'Insert Image into Article' : imgPickerFor === 'card' ? 'Select Card Image' : 'Select Featured Image'}
                </h3>
                <button className="admin-img-btn" onClick={() => setShowImgPicker(false)}>
                  <i className="bi bi-x-lg"></i> Close
                </button>
              </div>
              {media.filter(m => m.type?.startsWith('image/')).length === 0 ? (
                <div style={{ textAlign:'center', padding:'32px', color:'var(--text-muted)' }}>
                  <i className="bi bi-image" style={{ fontSize:'2rem', display:'block', marginBottom:12 }}></i>
                  No images in library yet. Upload an image first.
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:12 }}>
                  {media.filter(m => m.type?.startsWith('image/')).map(m => (
                    <div key={m.id} onClick={() => {
                      if (imgPickerFor === 'featured')  setForm(f => ({...f, featuredImg: m.data}));
                      if (imgPickerFor === 'card')      setForm(f => ({...f, cardImg: m.data}));
                      if (imgPickerFor === 'inarticle') insertImgToContent(m.data);
                      else setShowImgPicker(false);
                      if (imgPickerFor !== 'inarticle') setShowImgPicker(false);
                      notify('Image selected!');
                    }} style={{ cursor:'pointer', border:'2px solid rgba(82,183,136,.2)', borderRadius:10, overflow:'hidden', transition:'border-color .2s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor='rgba(82,183,136,.7)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor='rgba(82,183,136,.2)'}
                    >
                      <img src={m.data} alt={m.name} style={{ width:'100%', height:110, objectFit:'cover', display:'block' }} />
                      <div style={{ padding:'6px 8px', fontSize:'.7rem', color:'var(--text-muted)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════ SETTINGS ══════════════ */}
        {activeTab === 'settings' && (
          <div className="admin-page">
            <div className="admin-page-header">
              <h2 className="admin-page-title"><i className="bi bi-gear"></i> Site Settings</h2>
            </div>

            <div className="row g-4">
              <div className="col-lg-6">
                <div className="admin-panel-box mb-3">
                  <div className="admin-panel-title" style={{ fontSize: '1rem' }}><i className="bi bi-globe"></i> General</div>
                  {[
                    { key: 'siteTitle',   label: 'Site Title',    ph: 'CropGuard AI' },
                    { key: 'tagline',     label: 'Tagline',       ph: 'Protecting Crops with AI' },
                    { key: 'heroTitle',   label: 'Hero Heading',  ph: 'AI-Powered Crop Disease Detection' },
                    { key: 'heroSubtitle',label: 'Hero Subtitle', ph: 'Upload a leaf photo...' },
                    { key: 'footerText',  label: 'Footer Text',   ph: 'Final Year Project...' },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom: 14 }}>
                      <label className="admin-label">{f.label}</label>
                      <input className="admin-input" placeholder={f.ph}
                        value={settForm[f.key] || ''} onChange={e => setSettForm({ ...settForm, [f.key]: e.target.value })} />
                    </div>
                  ))}
                  <div style={{ marginBottom: 14 }}>
                    <label className="admin-label">Posts Per Page</label>
                    <select className="admin-input" value={settForm.postsPerPage} onChange={e => setSettForm({ ...settForm, postsPerPage: Number(e.target.value) })}>
                      {[3, 6, 9, 12].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="admin-panel-box mb-3">
                  <div className="admin-panel-title" style={{ fontSize: '1rem' }}><i className="bi bi-telephone"></i> Contact</div>
                  {[
                    { key: 'contactEmail', label: 'Contact Email', type: 'email', ph: 'cropguard@iub.edu.pk' },
                    { key: 'contactPhone', label: 'Contact Phone', type: 'tel',   ph: '+92-300-0000000' },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom: 14 }}>
                      <label className="admin-label">{f.label}</label>
                      <input className="admin-input" type={f.type} placeholder={f.ph}
                        value={settForm[f.key] || ''} onChange={e => setSettForm({ ...settForm, [f.key]: e.target.value })} />
                    </div>
                  ))}
                </div>

                <div className="admin-panel-box mb-3">
                  <div className="admin-panel-title" style={{ fontSize: '1rem' }}><i className="bi bi-share"></i> Social Media</div>
                  {[
                    { key: 'socialFacebook',  label: 'Facebook URL',  icon: 'bi-facebook' },
                    { key: 'socialTwitter',   label: 'Twitter / X URL',icon: 'bi-twitter-x' },
                    { key: 'socialInstagram', label: 'Instagram URL', icon: 'bi-instagram' },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom: 14 }}>
                      <label className="admin-label"><i className={`bi ${f.icon} me-1`}></i>{f.label}</label>
                      <input className="admin-input" placeholder="https://..."
                        value={settForm[f.key] || ''} onChange={e => setSettForm({ ...settForm, [f.key]: e.target.value })} />
                    </div>
                  ))}
                </div>

                <div className="admin-panel-box">
                  <div className="admin-panel-title" style={{ fontSize: '1rem' }}><i className="bi bi-toggles"></i> Toggles</div>
                  {[
                    { key: 'maintenanceMode', label: '🚧 Maintenance Mode', desc: 'Show maintenance page to visitors' },
                    { key: 'analyticsEnabled', label: '📊 Analytics', desc: 'Track page views and interactions' },
                  ].map(t => (
                    <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', marginBottom: 14 }}>
                      <input type="checkbox" checked={!!settForm[t.key]} onChange={e => setSettForm({ ...settForm, [t.key]: e.target.checked })}
                        style={{ width: 18, height: 18, accentColor: 'var(--green-accent)', flexShrink: 0 }} />
                      <div>
                        <div style={{ color: 'var(--cream)', fontSize: '.88rem', fontWeight: 600 }}>{t.label}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>{t.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <button className="admin-submit-btn" onClick={handleSaveSettings}>
                <i className="bi bi-check-circle"></i> Save All Settings
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Delete Confirm ── */}
      {confirmId && (
        <div className="admin-confirm">
          <div className="admin-confirm-box">
            <div style={{ fontSize: '2.5rem', marginBottom: 14 }}>🗑️</div>
            <h4 style={{ fontFamily: 'Playfair Display,serif', color: 'var(--cream)', marginBottom: 8 }}>Delete Post?</h4>
            <p style={{ color: 'var(--text-muted)', fontSize: '.9rem', marginBottom: 28 }}>
              This action cannot be undone.
            </p>
            <div className="d-flex gap-3 justify-content-center">
              <button className="admin-action-btn admin-delete-btn" style={{ padding: '10px 24px', fontSize: '.88rem' }} onClick={() => handleDelete(confirmId)}>
                <i className="bi bi-trash"></i> Delete
              </button>
              <button className="admin-action-btn admin-edit-btn" style={{ padding: '10px 24px', fontSize: '.88rem' }} onClick={() => setConfirmId(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast.msg && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast({ msg: '', type: 'success' })} />}
    </div>
  );
}
